// ============================================================
// Analytics v2 — daily rollups (event store → DB_ANALYTICS)
//
// Additive counters only (distincts/percentiles stay AE-only), UTC days,
// internal traffic excluded, idempotent upserts (a re-run overwrites a day).
// The reader answers `counters()` for ranges beyond the AE window.
// ============================================================
import { addDays, daysBetween, notAiCrawl, QueryInputError, resolveNumericField, sqliteDialect, } from './query/sql.js';
import { CUSTOM_METRIC_SLOTS } from './schema.js';
import { PRIVACY_DROP_EVENT } from './privacy.js';
export const ROLLUP_DIMS = [
    'host',
    'path',
    'referrer',
    'channel',
    'utm_source',
    'utm_campaign',
    'country',
    'device',
    'browser',
    'os',
    'item_id',
    'label',
    'd1',
    'd2',
    'd3',
    'd4',
];
const METRIC_COLS = Array.from({ length: CUSTOM_METRIC_SLOTS }, (_, i) => `m${i + 1}`);
const SUM_COLS = ['value', 'qty', 'duration_ms', ...METRIC_COLS];
const ROW_LIMIT = 100_000;
/** D1's SQLITE_MAX_COMPOUND_SELECT is small; stay under it. */
const SQLITE_UNION_TERMS = 4;
const chunk = (xs, size) => Array.from({ length: Math.ceil(xs.length / size) }, (_, i) => xs.slice(i * size, i * size + size));
export const ROLLUP_DDL = [
    `CREATE TABLE IF NOT EXISTS rollup_daily_events (
        project_id TEXT NOT NULL, sub_tenant TEXT NOT NULL, day TEXT NOT NULL, event TEXT NOT NULL,
        count REAL NOT NULL DEFAULT 0,
        ${SUM_COLS.map(c => `${c} REAL NOT NULL DEFAULT 0`).join(', ')},
        PRIMARY KEY (project_id, sub_tenant, day, event)
    )`,
    'CREATE INDEX IF NOT EXISTS idx_rde_project_day ON rollup_daily_events(project_id, day)',
    `CREATE TABLE IF NOT EXISTS rollup_daily_dims (
        project_id TEXT NOT NULL, sub_tenant TEXT NOT NULL, day TEXT NOT NULL, event TEXT NOT NULL,
        dim TEXT NOT NULL, key TEXT NOT NULL,
        count REAL NOT NULL DEFAULT 0, value REAL NOT NULL DEFAULT 0,
        PRIMARY KEY (project_id, sub_tenant, day, event, dim, key)
    )`,
    'CREATE INDEX IF NOT EXISTS idx_rdd_project_dim_day ON rollup_daily_dims(project_id, dim, day)',
    `CREATE TABLE IF NOT EXISTS rollup_runs (
        day TEXT PRIMARY KEY, ran_at INTEGER NOT NULL, rows_written INTEGER NOT NULL
    )`,
];
const n = (v) => {
    const x = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(x) ? x : 0;
};
function asTarget(t) {
    return 'dbFor' in t ? t : { dbFor: () => t, all: [t] };
}
/** Roll one UTC day of the event store into DB_ANALYTICS (each project's shard). Returns rows written. */
export async function rollupDay(store, target, day) {
    const route = asTarget(target);
    const d = store.dialect;
    const fromMs = Date.parse(`${day}T00:00:00Z`);
    const toMs = fromMs + 86_400_000;
    // AI crawler rows stay out of the rollups (their report reads the raw store only).
    const where = `WHERE ${d.timeRange(fromMs, toMs, day, day, 'UTC')} AND ${d.col('internal')} = 0 AND ${notAiCrawl(d)}`;
    const always = '1 = 1';
    const groupBase = `${d.col('project_id')} AS project_id, ${d.col('sub_tenant')} AS sub_tenant, ${d.col('event')} AS event`;
    const events = await store.query(`SELECT ${groupBase},
  ${d.countIf(always)} AS count,
  ${SUM_COLS.map(c => `${d.sumIf(d.col(c), always)} AS ${c}`).join(',\n  ')}
FROM ${d.table} ${where}
GROUP BY project_id, sub_tenant, event
LIMIT ${ROW_LIMIT}`);
    const dimSelect = (dim, tagged = false) => `SELECT ${groupBase},${tagged ? ` ${d.lit(dim)} AS dim,` : ''} ${d.col(dim)} AS key,
  ${d.countIf(always)} AS count, ${d.sumIf(d.col('value'), always)} AS value
FROM ${d.table} ${where} AND ${d.col(dim)} != ''
GROUP BY project_id, sub_tenant, event, key
LIMIT ${ROW_LIMIT}`;
    // SQLite (D1 store): dimensions in UNION ALL chunks (D1 caps compound SELECTs at a few
    // terms) — 4 round trips instead of 16. Analytics Engine keeps one query per dimension
    // (its SQL is pinned by tests).
    const dimRows = d.name === 'sqlite'
        ? (await Promise.all(chunk(ROLLUP_DIMS, SQLITE_UNION_TERMS).map(dims => store.query(dims
            .map(dim => `SELECT * FROM (${dimSelect(dim, true)})`)
            .join('\nUNION ALL\n'))))).flat()
        : (await Promise.all(ROLLUP_DIMS.map(dim => store
            .query(dimSelect(dim))
            .then(rows => rows.map(r => ({ ...r, dim })))))).flat();
    const evSql = `INSERT INTO rollup_daily_events (project_id, sub_tenant, day, event, count, ${SUM_COLS.join(', ')})
VALUES (?, ?, ?, ?, ?, ${SUM_COLS.map(() => '?').join(', ')})
ON CONFLICT (project_id, sub_tenant, day, event) DO UPDATE SET count = excluded.count, ${SUM_COLS.map(c => `${c} = excluded.${c}`).join(', ')}`;
    const dimSql = `INSERT INTO rollup_daily_dims (project_id, sub_tenant, day, event, dim, key, count, value)
VALUES (?, ?, ?, ?, ?, ?, ?, ?)
ON CONFLICT (project_id, sub_tenant, day, event, dim, key) DO UPDATE SET count = excluded.count, value = excluded.value`;
    const stmtsByDb = new Map();
    for (const db of route.all) {
        // Imported history (rollup_imports, e.g. GA4) is never dropped by a re-run of its day.
        const keepImports = (await db
            .prepare("SELECT 1 AS ok FROM sqlite_master WHERE type = 'table' AND name = 'rollup_imports'")
            .first())
            ? ' AND project_id NOT IN (SELECT project_id FROM rollup_imports WHERE day = ?)'
            : '';
        const del = (table) => keepImports
            ? db.prepare(`DELETE FROM ${table} WHERE day = ?${keepImports}`).bind(day, day)
            : db.prepare(`DELETE FROM ${table} WHERE day = ?`).bind(day);
        // Idempotent: every shard drops the day's rows first so keys that vanished (e.g. after
        // sampling) don't linger — even a shard with no rows left.
        stmtsByDb.set(db, [del('rollup_daily_events'), del('rollup_daily_dims')]);
    }
    const push = (projectId, stmt) => {
        const db = route.dbFor(projectId);
        const list = stmtsByDb.get(db);
        if (!list) {
            throw new Error('rollup target: dbFor returned a database missing from all');
        }
        list.push(stmt(db));
    };
    for (const r of events) {
        push(String(r.project_id), db => db
            .prepare(evSql)
            .bind(String(r.project_id), String(r.sub_tenant ?? ''), day, String(r.event), n(r.count), ...SUM_COLS.map(c => n(r[c]))));
    }
    for (const r of dimRows) {
        push(String(r.project_id), db => db
            .prepare(dimSql)
            .bind(String(r.project_id), String(r.sub_tenant ?? ''), day, String(r.event), String(r.dim), String(r.key), n(r.count), n(r.value)));
    }
    for (const [db, stmts] of stmtsByDb) {
        stmts.push(db
            .prepare(`INSERT INTO rollup_runs (day, ran_at, rows_written) VALUES (?, ?, ?)
ON CONFLICT (day) DO UPDATE SET ran_at = excluded.ran_at, rows_written = excluded.rows_written`)
            .bind(day, Date.now(), stmts.length - 2));
        for (let i = 0; i < stmts.length; i += 400) {
            await db.batch(stmts.slice(i, i + 400));
        }
    }
    let privacyRows = 0;
    try {
        privacyRows = await rollupPrivacyDay(store, route, day);
    }
    catch (err) {
        // Separate from the counters above: needs analytics migration 0007.
        console.error(`[rollup] privacy ${day} failed`, err);
    }
    return events.length + dimRows.length + privacyRows;
}
export const PRIVACY_ROLLUP_DDL = [
    `CREATE TABLE IF NOT EXISTS rollup_daily_privacy (
        project_id TEXT NOT NULL, sub_tenant TEXT NOT NULL, day TEXT NOT NULL,
        kind TEXT NOT NULL, flags REAL NOT NULL, server REAL NOT NULL DEFAULT 0,
        count REAL NOT NULL DEFAULT 0,
        PRIMARY KEY (project_id, sub_tenant, day, kind, flags, server)
    )`,
    'CREATE INDEX IF NOT EXISTS idx_rdp_project_day ON rollup_daily_privacy(project_id, day)',
];
/**
 * Opt-out evidence counters for one UTC day (query/proof.ts): accepted events by privacy
 * flags (internal traffic included — it is still processed data) + dropped-envelope markers.
 * Rows are bounded by the distinct flag combinations, not by traffic.
 */
export async function rollupPrivacyDay(store, target, day) {
    const route = asTarget(target);
    const d = store.dialect;
    const fromMs = Date.parse(`${day}T00:00:00Z`);
    const range = d.timeRange(fromMs, fromMs + 86_400_000, day, day, 'UTC');
    const ev = d.col('event');
    const base = `${d.col('project_id')} AS project_id, ${d.col('sub_tenant')} AS sub_tenant`;
    const [accepted, dropped] = await Promise.all([
        store.query(`SELECT ${base}, ${d.col('reserved')} AS flags, ${d.col('server')} AS server,
  ${d.countIf('1 = 1')} AS count
FROM ${d.table} WHERE ${range} AND ${ev} != 'purchase_item' AND ${ev} != '${PRIVACY_DROP_EVENT}' AND ${notAiCrawl(d)}
GROUP BY project_id, sub_tenant, flags, server
LIMIT ${ROW_LIMIT}`),
        store.query(`SELECT ${base}, ${d.col('label')} AS reason, ${d.col('reserved')} AS flags,
  ${d.sumIf(d.col('qty'), '1 = 1')} AS count
FROM ${d.table} WHERE ${range} AND ${ev} = '${PRIVACY_DROP_EVENT}' AND ${notAiCrawl(d)}
GROUP BY project_id, sub_tenant, reason, flags
LIMIT ${ROW_LIMIT}`),
    ]);
    const insSql = `INSERT INTO rollup_daily_privacy (project_id, sub_tenant, day, kind, flags, server, count)
VALUES (?, ?, ?, ?, ?, ?, ?)
ON CONFLICT (project_id, sub_tenant, day, kind, flags, server) DO UPDATE SET count = excluded.count`;
    // Every shard drops the day first (idempotent re-runs), then gets its projects' rows.
    const stmtsByDb = new Map(route.all.map(db => [
        db,
        [db.prepare('DELETE FROM rollup_daily_privacy WHERE day = ?').bind(day)],
    ]));
    const push = (r, kind, server) => {
        const db = route.dbFor(String(r.project_id));
        stmtsByDb
            .get(db)
            ?.push(db
            .prepare(insSql)
            .bind(String(r.project_id), String(r.sub_tenant ?? ''), day, kind, n(r.flags), server, n(r.count)));
    };
    accepted.forEach(r => push(r, 'accepted', n(r.server)));
    dropped.forEach(r => push(r, `drop:${String(r.reason)}`, 0));
    for (const [db, stmts] of stmtsByDb) {
        for (let i = 0; i < stmts.length; i += 400) {
            await db.batch(stmts.slice(i, i + 400));
        }
    }
    return accepted.length + dropped.length;
}
/**
 * Daily job: roll up YESTERDAY (UTC) once. Rollups are only read for ranges older than the
 * AE window (88 days), so partial days are never needed — and rewriting them hourly would
 * multiply D1 row writes (deletes count as writes) by ~48 for no benefit.
 */
export async function runDailyRollups(store, db, now = new Date()) {
    const today = now.toISOString().slice(0, 10);
    const results = {};
    for (const day of [addDays(today, -1)]) {
        try {
            results[day] = await rollupDay(store, db, day);
        }
        catch (err) {
            console.error(`[rollup] ${day} failed`, err);
            results[day] = `error: ${String(err)}`;
        }
    }
    return results;
}
/** Delete rollup rows older than each project's retention. */
export async function purgeRollups(db, projectId, retentionDays, now = new Date()) {
    const cutoff = addDays(now.toISOString().slice(0, 10), -retentionDays);
    await db.batch([
        db
            .prepare('DELETE FROM rollup_daily_events WHERE project_id = ? AND day < ?')
            .bind(projectId, cutoff),
        db
            .prepare('DELETE FROM rollup_daily_dims WHERE project_id = ? AND day < ?')
            .bind(projectId, cutoff),
    ]);
}
export class D1RollupReader {
    db;
    config;
    dialect = sqliteDialect();
    lit = (v) => this.dialect.lit(v);
    constructor(db, config) {
        this.db = db;
        this.config = config;
    }
    async counters(p, specs, opts) {
        if (opts.interval && opts.interval !== 'day') {
            throw new QueryInputError('only daily intervals are available beyond 88 days');
        }
        if (daysBetween(p.from, p.to) > 1830) {
            throw new QueryInputError('range too long');
        }
        const filters = [...p.filters];
        let dimFilter = null;
        if (filters.length > 1) {
            throw new QueryInputError('only one filter is supported beyond 88 days');
        }
        if (filters.length === 1) {
            const f = filters[0];
            if (f.op !== 'eq') {
                throw new QueryInputError('only eq filters are supported beyond 88 days');
            }
            const col = this.dimColumn(f.dim);
            dimFilter = { dim: col, value: f.value };
        }
        const useDims = !!opts.groupBy || !!dimFilter;
        if (opts.groupBy && dimFilter && opts.groupBy !== dimFilter.dim) {
            throw new QueryInputError('filter and breakdown must use the same dimension beyond 88 days');
        }
        if (opts.groupBy === 'event' || opts.groupBy === 'sub_tenant') {
            return this.groupByCore(p, specs, opts.groupBy, opts.limit);
        }
        const table = useDims ? 'rollup_daily_dims' : 'rollup_daily_events';
        const where = [
            `project_id = ${this.lit(p.projectId)}`,
            `day >= ${this.lit(p.from)}`,
            `day <= ${this.lit(p.to)}`,
        ];
        if (p.subTenant !== null) {
            where.push(`sub_tenant = ${this.lit(p.subTenant)}`);
        }
        if (useDims) {
            where.push(`dim = ${this.lit(opts.groupBy ?? dimFilter.dim)}`);
        }
        if (dimFilter) {
            where.push(`key = ${this.lit(dimFilter.value)}`);
        }
        const selects = [];
        const groups = [];
        if (opts.groupBy) {
            selects.push('key');
            groups.push('key');
        }
        if (opts.interval) {
            selects.push('day AS t');
            groups.push('day');
        }
        const nulls = [];
        for (const s of specs) {
            const expr = this.aggExpr(s, useDims);
            if (expr === null) {
                nulls.push(s.as);
                continue;
            }
            selects.push(`${expr} AS ${s.as}`);
        }
        if (selects.length === 0) {
            selects.push('0 AS _none');
        }
        const firstReal = specs.find(s => !nulls.includes(s.as));
        const sql = `SELECT ${selects.join(', ')} FROM ${table} WHERE ${where.join(' AND ')}${groups.length ? ` GROUP BY ${groups.join(', ')}` : ''}${opts.interval ? ' ORDER BY day' : opts.groupBy && firstReal ? ` ORDER BY ${firstReal.as} DESC` : ''}${groups.length ? ` LIMIT ${opts.limit}` : ''}`;
        const { results } = await this.db.prepare(sql).all();
        return (results ?? []).map(r => {
            const out = {};
            if (opts.groupBy) {
                out.key = String(r.key);
            }
            if (opts.interval) {
                out.t = String(r.t);
            }
            for (const s of specs) {
                out[s.as] = nulls.includes(s.as) ? null : n(r[s.as]);
            }
            return out;
        });
    }
    dimColumn(dim) {
        if (dim.startsWith('d:')) {
            const slot = this.config.dimensions.find(d => d.name === dim.slice(2))?.slot;
            if (slot) {
                return `d${slot}`;
            }
        }
        if (ROLLUP_DIMS.includes(dim)) {
            return dim;
        }
        throw new QueryInputError(`dimension '${dim}' is not available beyond 88 days`);
    }
    eventCond(s) {
        const events = s.event === undefined ? [] : Array.isArray(s.event) ? s.event : [s.event];
        if (events.length === 0) {
            return "event != 'purchase_item'";
        }
        return `event IN (${events.map(e => this.lit(e)).join(', ')})`;
    }
    aggExpr(s, dims) {
        if (s.match && Object.keys(s.match).length > 0) {
            throw new QueryInputError(`counter '${s.as}': match conditions are not available beyond 88 days`);
        }
        if (s.agg !== 'count' && s.agg !== 'sum') {
            // Distincts and quantiles are raw-store only.
            return null;
        }
        if (s.agg === 'count') {
            return `SUM(CASE WHEN ${this.eventCond(s)} THEN count ELSE 0 END)`;
        }
        const col = resolveNumericField(s.field ?? 'value', this.config);
        if (dims && col !== 'value') {
            throw new QueryInputError(`counter '${s.as}': only value sums are available in breakdowns beyond 88 days`);
        }
        return `SUM(CASE WHEN ${this.eventCond(s)} THEN ${col} ELSE 0 END)`;
    }
    async groupByCore(p, specs, col, limit) {
        const where = [
            `project_id = ${this.lit(p.projectId)}`,
            `day >= ${this.lit(p.from)}`,
            `day <= ${this.lit(p.to)}`,
        ];
        if (p.subTenant !== null) {
            where.push(`sub_tenant = ${this.lit(p.subTenant)}`);
        }
        const selects = [`${col} AS key`];
        const nulls = [];
        for (const s of specs) {
            const e = this.aggExpr(s, false);
            if (e === null) {
                nulls.push(s.as);
            }
            else {
                selects.push(`${e} AS ${s.as}`);
            }
        }
        const first = specs.find(s => !nulls.includes(s.as));
        const { results } = await this.db
            .prepare(`SELECT ${selects.join(', ')} FROM rollup_daily_events WHERE ${where.join(' AND ')} GROUP BY key${first ? ` ORDER BY ${first.as} DESC` : ''} LIMIT ${limit}`)
            .all();
        return (results ?? []).map(r => {
            const out = { key: String(r.key) || '(none)' };
            for (const s of specs) {
                out[s.as] = nulls.includes(s.as) ? null : n(r[s.as]);
            }
            return out;
        });
    }
}
//# sourceMappingURL=rollup.js.map